import _ from 'underscore';
import React from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { whichIsClosest } from './util';

/**
 * HoC that keeps track of a parent ref and a list of child refs, and
 * provides a "doWithClosest" callback handler factory. This
 * factory can be used to generate event handlers that will call the given
 * function ("actionWithIndex") with the index of the closest child ref (to
 * the location of the mouse click) as the first argument.
 *
 * NOTE: MUST call props.setChildCount() at start of render()!
 *
 * @param {React.Component} WrappedComponent - component to wrap
 * @returns {React.Component} an enhanced version of the argument
 */
export const WithDoWithClosestHandler = (WrappedComponent) => {
  class HoC extends React.PureComponent {
    static displayName = `WithDoWithClosestHandler(${getDisplayName(WrappedComponent)})`;

    constructor(props) {
      super(props);

      this.containerRef = null;
      this.childRefs = [];
    }

    setChildRefsFactory = index => (ref) => {
      if (ref && index >= 0 && index < this.childRefs.length) {
        this.childRefs[index] = ref;
      }
    };

    setContainerRef = (ref) => { this.containerRef = ref; };

    setChildRefsCount = (count) => { this.childRefs = new Array(count); };

    doWithClosest = action => (e) => {
      if (e) { e.persist(); }

      const args = {
        index: -1,
        ref: null,
      };

      if (this.containerRef && this.childRefs) {
        const numChildren = this.childRefs.length;

        if (numChildren === 1) {
          args.index = 0;
          args.ref = _.first(this.childRefs);
        } else {
          const click = {
            x: e.clientX,
            y: e.clientY,
          };
          const inputInfo = [];

          for (let i = 0; i < numChildren; i += 1) {
            const el = this.childRefs[i];

            if (el) { // Only search through active refs
              const rect = el.getBoundingClientRect();

              inputInfo.push({
                index: i,
                ref: el,
                coordinates: [
                  { x: rect.left, y: rect.top },
                  { x: rect.left + rect.width, y: rect.top + rect.height },
                ],
              });
            }
          }

          const inputCoordinates = _.map(inputInfo, _.property('coordinates'));
          const closestCoordinates = whichIsClosest(click, inputCoordinates);

          args.index = inputInfo[closestCoordinates].index;
          args.ref = inputInfo[closestCoordinates].ref;
        }
      }

      action(args, e);
    };

    render() {
      return (
        <WrappedComponent
          containerRef={this.setContainerRef}
          setChildRefCount={this.setChildRefsCount}
          setChildRefByIndex={this.setChildRefsFactory}
          doWithClosest={this.doWithClosest}
          {...this.props}
        />
      );
    }
  }

  return hoistNonReactStatics(HoC, WrappedComponent);
};
